<div class="vc-landing-container">
    <div class="vc-landing-header-row">
        <h1 class="vc-landing-header h1-heading">{{ 'COMMON_UI.DASHBOARD' | transloco }}</h1>
        @if (showRiskDashboardCta) {
            <div class="vc-landing-risk-cta">
                <span class="h3-heading vc-landing-risk-cta-title">{{
                    'LANDING.DASHBOARD.RISK_CTA.TITLE' | transloco
                }}</span>
                <vc-button
                    mode="basic"
                    size="small"
                    iconPrefix="arrow_forward"
                    [label]="'LANDING.DASHBOARD.RISK_CTA.BUTTON' | transloco"
                    (trigger)="openRiskDashboard()">
                </vc-button>
            </div>
        }
    </div>
    <div class="vc-landing-widgets-wrapper vc-grid-layout">
        <!-- Assets Count -->
        <vc-chart-wrapper
            class="col-4 space-pd-top-0 space-pd-left-0"
            contentHeight="299px"
            [loading]="assetCountLoader">
            <h2 chartActionsTitle class="h3-heading vc-landing-align-left">
                {{ 'LANDING.DASHBOARD.TITLE.ASSET_COUNT' | transloco }}
            </h2>
            <span chartActionsRightSide class="body-medium vc-landing-align-right">{{
                'LANDING.DASHBOARD.LAST_30_DAYS' | transloco
            }}</span>
            <div class="vc-landing-chart-count">
                @if (assetsTotalCount) {
                    <div class="display-small">{{ assetsTotalCount | number }}</div>
                    <vc-button
                        [label]="'LANDING.DASHBOARD.VIEW_ALL_ASSETS' | transloco"
                        mode="text"
                        (trigger)="navigateToAssetSearch()">
                    </vc-button>
                } @else {
                    <vc-chart-empty
                        class="vc-landing-chart-count-empty"
                        [noDataMsg]="'COMMON_UI.TABLE.NO_DATA_AVAILABLE' | transloco" />
                }
            </div>
        </vc-chart-wrapper>

        <!-- Assets By Type Chart -->
        <vc-pie-chart
            class="col-4 space-pd-top-0"
            [title]="'LANDING.CHART.TITLE.ASSETS_BY_TYPE' | transloco"
            [contentHeight]="'299px'"
            [loading]="assetsByTypeLoader"
            [data]="assetsByType"
            [legendOptions]="pieChartLegends"
            [pointClick]="navigateToAssetByType">
            <ng-template #chartWrapperEnd>
                <span class="body-medium vc-landing-align-right">{{
                    'LANDING.DASHBOARD.LAST_30_DAYS' | transloco
                }}</span></ng-template
            >
        </vc-pie-chart>

        <!-- Riskiest Assets Widget -->
        <vc-chart-wrapper
            class="col-4 space-pd-top-0 space-pd-right-0"
            contentHeight="299px"
            [loading]="riskiestAssetLoader">
            <h2 chartActionsTitle class="h3-heading vc-landing-title">
                {{ 'LANDING.DASHBOARD.TITLE.RISKIEST_ASSETS' | transloco }}
            </h2>
            <span chartActionsRightSide class="body-medium vc-landing-align-right">{{
                'LANDING.DASHBOARD.LAST_30_DAYS' | transloco
            }}</span>
            <div class="vc-landing-riskiest-assets-items">
                @for (asset of riskiestAssets; track asset) {
                    <div class="vc-landing-chart-asset">
                        <vc-icon
                            [name]="assetTypeIconRenderer(asset.type)"
                            [tooltip]="assetTypeItemRenderer(asset.type)"
                            color="var(--primary-600)"></vc-icon>
                        <vc-link
                            class="body-medium"
                            padding="12px"
                            width="calc(100% - 24px)"
                            style="width: calc(100% - 32px)"
                            mode="text"
                            [label]="asset.displayName"
                            [tooltip]="asset.displayName"
                            [target]="'_self'"
                            [truncate]="true"
                            (trigger)="onRiskiestAssetClick(asset.id)">
                        </vc-link>
                    </div>
                } @empty {
                    <vc-chart-empty
                        style="height: 100%"
                        [noDataMsg]="'COMMON_UI.TABLE.NO_DATA_AVAILABLE' | transloco"></vc-chart-empty>
                }
            </div>
        </vc-chart-wrapper>

        <!-- Findings by severity -->
        <vc-findings-by-severity
            class="col-6 space-pd-left-0"
            [title]="'LANDING.CHART.TITLE.FINDINGS_BY_SEVERITY' | transloco"
            contentHeight="320px"
            [loading]="findingChartLoader"
            [facets]="findingsBySeverityFacets"
            [onPointClick]="navigateToFindingsBySeverity">
            <ng-template #chartWrapperEnd>
                <vc-select
                    class="vc-landing-timeframe-selector space-pd-0"
                    [label]="'LANDING.TIME_FRAME' | transloco"
                    [minimalStyles]="true"
                    [(value)]="findingsBySeverityTimeFrame"
                    [options]="intervals"
                    [itemRenderer]="intervalsItemRenderer"
                    (valueChange)="
                        landingChartType.findingsBySeverity !== findingsBySeverityTimeFrame &&
                            fetchFindingsBySeverityChart($event)
                    "></vc-select>
            </ng-template>
        </vc-findings-by-severity>

        <!-- Findings by categories -->
        <vc-findings-by-category
            class="col-6 space-pd-right-0"
            [title]="'LANDING.CHART.TITLE.FINDINGS_BY_CATEGORIES' | transloco"
            contentHeight="320px"
            [loading]="findingChartLoader"
            [updating]="!findingChartLoader"
            [facets]="findingsByCategoryFacets"
            [onSeriesClick]="navigateToFindingsByCategory">
            <vc-select
                class="vc-landing-timeframe-selector space-pd-0"
                chartActionsRightSide
                [label]="'LANDING.TIME_FRAME' | transloco"
                [minimalStyles]="true"
                [(value)]="findingsByCategoryTimeFrame"
                [options]="intervals"
                [itemRenderer]="intervalsItemRenderer"
                (valueChange)="
                    landingChartType.findingsByCategories !== findingsByCategoryTimeFrame &&
                        fetchFindingsByCategoriesChart($event)
                "></vc-select>
        </vc-findings-by-category>

        <!-- Events/Findings Chart -->
        <vc-events-findings-chart
            class="col-12 space-pd-x-0 space-pd-bottom-0"
            [title]="'LANDING.CHART.TITLE.EVENTS/FINDINGS' | transloco"
            contentHeight="299px"
            [loading]="eventFindingChartLoader"
            [data]="eventsFindingData"
            [xAxisTitle]="eventsFindingXAxisTitle"
            [categoryFormatter]="eventsFindingCategoryFormatter"
            (seriesClick)="onEventsFindingsSeriesClick($event)">
            <vc-select
                chartActionsRightSide
                [label]="'LANDING.TIME_FRAME' | transloco"
                [minimalStyles]="true"
                [(value)]="landingChartType.eventFindingChart"
                [options]="intervals"
                [itemRenderer]="intervalsItemRenderer"
                (valueChange)="fetchEventFindingChart($event)"></vc-select>
        </vc-events-findings-chart>
    </div>
</div>
